import '../inputs/neb-select';
import '../inputs/neb-textarea';
import '../neb-button';
import '../../../../../src/components/controls/inputs/neb-checkbox';

import { css, html, LitElement } from 'lit';

import { ERA_WARNINGS } from '../../../../../src/utils/era';
import {
  getReports,
  getReport,
  processEraWithWarnings,
  generateUpdatedReport,
} from '../../../../neb-api-client/src/electronic-claims/reports';
import { baseStyles } from '../../../../neb-styles/neb-styles';
import {
  CSS_COLOR_WHITE,
  CSS_SPACING,
} from '../../../../neb-styles/neb-variables';

const ELEMENTS = {
  eraReportSelect: { id: 'era-report-select' },
  eraTreeView: { id: 'era-tree-view' },
  eraWarningsContainer: { id: 'era-warnings-container' },
  getUpdatedReportButton: { id: 'get-updated-report-button' },
  processReportButton: { id: 'process-report-button' },
  updatedReportTextArea: { id: 'updated-report-textarea' },
};

class NebEraWarningsPlayground extends LitElement {
  static get properties() {
    return {
      __eraReports: Array,
      __selectedEraReport: Object,
      __eraTreeData: Object,
      __selectedNode: Object,
      __selectedNodeType: String,
      __selectedWarnings: Object,
      __updatedReport: String,
      __loading: Boolean,
      __processing: Boolean,
      __generatingReport: Boolean,
    };
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
    this.__loadEraReports();
  }

  __initState() {
    this.__eraReports = [];
    this.__selectedEraReport = null;
    this.__eraTreeData = {};
    this.__selectedNode = null;
    this.__selectedNodeType = '';
    this.__selectedWarnings = {};
    this.__updatedReport = '';
    this.__loading = false;
    this.__processing = false;
    this.__generatingReport = false;

    // Initialize era warnings reference
    this.__eraWarnings = ERA_WARNINGS;
  }

  async __loadEraReports() {
    try {
      this.__loading = true;
      this.requestUpdate();

      // Get list of report IDs
      const reportsResponse = await getReports();
      const reportIds = reportsResponse.reports || [];

      // Fetch details for each report
      const reportPromises = reportIds
        .filter(report => report.startsWith('R5'))
        .map(async reportId => {
          try {
            const reportData = await getReport(reportId);
            return {
              id: reportId,
              name: `ERA Report ${reportId}`,
              data: reportData,
            };
          } catch (error) {
            console.error(`Failed to load report ${reportId}:`, error);
            return null;
          }
        });

      const reports = await Promise.all(reportPromises);
      this.__eraReports = reports.filter(report => report !== null);
    } catch (error) {
      console.error('Failed to load ERA reports:', error);
      this.__eraReports = [];
    } finally {
      this.__loading = false;
      this.requestUpdate();
    }
  }

  __initHandlers() {
    this.__handlers = {
      selectEraReport: event => {
        const { value } = event.target;
        const selectedReport = this.__eraReports.find(
          report => report.id === value,
        );
        this.__selectedEraReport = selectedReport;

        if (selectedReport) {
          this.__buildEraTreeData();
        }
        this.__selectedWarnings = {};
        this.__updatedReport = '';
        this.requestUpdate();
      },
      selectEraNode: (key, nodePath) => {
        this.__selectedNode = { key, nodePath };
        this.__selectedNodeType = this.__getNodeType(nodePath);
      },
      toggleWarning: (warningId, checked) => {
        const { nodePath } = this.__selectedNode || {};
        if (!nodePath || nodePath.length === 0) return;

        const [, claimKey, chargeKey] = nodePath;

        if (nodePath.length === 2) {
          this.__toggleClaimLevelWarning(claimKey, warningId, checked);
        } else if (nodePath.length === 3) {
          this.__toggleChargeLevelWarning(
            claimKey,
            chargeKey,
            warningId,
            checked,
          );
        }

        // Create a new object reference to trigger reactivity
        this.__selectedWarnings = { ...this.__selectedWarnings };
        this.requestUpdate();
      },
      getUpdatedReport: async () => {
        await this.__generateUpdatedReport();
      },
      processReport: async () => {
        if (!this.__selectedEraReport) {
          console.error('No report selected');
          return;
        }

        try {
          this.__processing = true;
          this.requestUpdate();

          const result = await processEraWithWarnings(
            this.__selectedEraReport.id,
            this.__updatedReport,
          );

          console.log('Report processed successfully:', result);

          // You could show a success message or update the UI as needed
          this.__updatedReport = 'Report processed successfully!';
        } catch (error) {
          console.error('Failed to process report:', error);
          this.__updatedReport =
            'Failed to process report. Check console for details.';
        } finally {
          this.__processing = false;
          this.requestUpdate();
        }
      },
    };
  }

  __getNodeType(nodePath) {
    if (!nodePath || nodePath.length === 0) return '';
    if (nodePath.length === 2) return 'CLAIM_LEVEL';
    if (nodePath.length === 3) return 'LINE_ITEM_LEVEL';
    return '';
  }

  __toggleClaimLevelWarning(claimKey, warningId, checked) {
    if (!this.__selectedWarnings.claims) {
      this.__selectedWarnings.claims = {};
    }

    if (!this.__selectedWarnings.claims[claimKey]) {
      this.__selectedWarnings.claims[claimKey] = {
        warnings: [],
        charges: {},
      };
    }

    const { warnings } = this.__selectedWarnings.claims[claimKey];
    this.__updateWarningsList(warnings, warningId, checked);
  }

  __toggleChargeLevelWarning(claimKey, chargeKey, warningId, checked) {
    if (!this.__selectedWarnings.claims) {
      this.__selectedWarnings.claims = {};
    }

    if (!this.__selectedWarnings.claims[claimKey]) {
      this.__selectedWarnings.claims[claimKey] = {
        warnings: [],
        charges: {},
      };
    }

    if (!this.__selectedWarnings.claims[claimKey].charges) {
      this.__selectedWarnings.claims[claimKey].charges = {};
    }

    if (!this.__selectedWarnings.claims[claimKey].charges[chargeKey]) {
      this.__selectedWarnings.claims[claimKey].charges[chargeKey] = {
        warnings: [],
      };
    }

    const { warnings } =
      this.__selectedWarnings.claims[claimKey].charges[chargeKey];
    this.__updateWarningsList(warnings, warningId, checked);
  }

  __updateWarningsList(warningsList, warningId, checked) {
    if (checked) {
      if (!warningsList.includes(warningId)) {
        warningsList.push(warningId);
      }
    } else {
      const index = warningsList.indexOf(warningId);

      if (index > -1) {
        warningsList.splice(index, 1);
      }
    }
  }

  __getSelectedWarningsForNode(nodePath) {
    if (!nodePath?.length || !this.__selectedWarnings.claims) {
      return [];
    }

    const [, claimKey, chargeKey] = nodePath;
    const claim = this.__selectedWarnings.claims[claimKey];

    if (!claim) return [];

    return this.__extractWarningsFromClaim(claim, nodePath.length, chargeKey);
  }

  __extractWarningsFromClaim(claim, pathLength, chargeKey) {
    if (pathLength === 2) {
      return claim.warnings || [];
    }

    if (pathLength === 3 && claim.charges?.[chargeKey]) {
      return claim.charges[chargeKey].warnings || [];
    }

    return [];
  }

  __buildEraTreeData() {
    if (!this.__selectedEraReport || !this.__selectedEraReport.data) {
      this.__eraTreeData = {};
      return;
    }

    const report = this.__selectedEraReport.data;
    const treeData = {};

    report.data.transactions.forEach((transaction, transactionIndex) => {
      const transactionKey = `Transaction ${transactionIndex + 1}`;
      treeData[transactionKey] = {};

      transaction.detailInfo.forEach((detail, _detailIndex) => {
        detail.paymentInfo.forEach((payment, _paymentIndex) => {
          const claimKey = `Claim - ${payment.claimPaymentInfo.patientControlNumber} - ${payment.patientName.lastName},${payment.patientName.firstName}`;
          treeData[transactionKey][claimKey] = {};

          payment.serviceLines.forEach((serviceLine, _serviceIndex) => {
            const lineItemKey = `Charge - ${_serviceIndex} - ${serviceLine.serviceDate} - ${serviceLine.servicePaymentInformation.adjudicatedProcedureCode} - ${serviceLine.servicePaymentInformation.unitsOfServicePaidCount} units`;
            treeData[transactionKey][claimKey][lineItemKey] = serviceLine;
          });
        });
      });
    });

    this.__eraTreeData = treeData;
  }

  __renderEraTree() {
    if (!this.__eraTreeData || Object.keys(this.__eraTreeData).length === 0) {
      return html`<div>No ERA data to display</div>`;
    }

    return html`
      <div class="tree-structure">
        ${Object.entries(this.__eraTreeData).map(
          ([transactionKey, claims]) => html`
            <div
              class="tree-node era-node"
              @click="${() =>
                this.__handlers.selectEraNode(transactionKey, [
                  transactionKey,
                ])}"
            >
              <strong>${transactionKey}</strong>
              <div class="tree-children">
                ${Object.entries(claims).map(
                  ([claimKey, lineItems]) => html`
                    <div
                      class="tree-node claim-node"
                      @click="${e => {
                        e.stopPropagation();
                        this.__handlers.selectEraNode(claimKey, [
                          transactionKey,
                          claimKey,
                        ]);
                      }}"
                    >
                      ${claimKey}
                      <div class="tree-children">
                        ${Object.entries(lineItems).map(
                          ([lineItemKey, _lineItem]) => html`
                            <div
                              class="tree-node line-item-node"
                              @click="${e => {
                                e.stopPropagation();
                                this.__handlers.selectEraNode(lineItemKey, [
                                  transactionKey,
                                  claimKey,
                                  lineItemKey,
                                ]);
                              }}"
                            >
                              ${lineItemKey}
                            </div>
                          `,
                        )}
                      </div>
                    </div>
                  `,
                )}
              </div>
            </div>
          `,
        )}
      </div>
    `;
  }

  __renderSelectedNodeInfo() {
    if (!this.__selectedNodeType) {
      return html``;
    }

    return html`
      <div class="selected-node-info">
        <strong>Selected: ${this.__selectedNode?.key}</strong>
        <div>Level: ${this.__selectedNodeType}</div>
      </div>
    `;
  }

  __renderWarningsList() {
    if (!this.__selectedNodeType) {
      return html`<div>Select a node to see available warnings</div>`;
    }

    const warningsForLevel = this.__eraWarnings[this.__selectedNodeType] || [];
    const selectedWarnings = this.__getSelectedWarningsForNode(
      this.__selectedNode?.nodePath,
    );

    if (warningsForLevel.length === 0) {
      return html`<div>No warnings available for this level</div>`;
    }

    return html`
      <div class="warnings-list">
        ${warningsForLevel.map(
          warning => html`
            <div class="warning-item">
              <neb-checkbox
                name="${warning.id}"
                label="${warning.label}"
                .checked="${selectedWarnings.includes(warning.id)}"
                .onChange="${e =>
                  this.__handlers.toggleWarning(warning.id, e.value)}"
              ></neb-checkbox>
            </div>
          `,
        )}
      </div>
    `;
  }

  async __generateUpdatedReport() {
    if (!this.__selectedEraReport) {
      this.__updatedReport = '';
      return;
    }

    try {
      this.__generatingReport = true;
      this.requestUpdate();

      const result = await generateUpdatedReport(
        this.__selectedEraReport.id,
        this.__selectedWarnings,
      );

      // The API should return the updated report JSON
      this.__updatedReport = JSON.stringify(result, null, 2);

      console.log('Updated report generated successfully');
    } catch (error) {
      console.error('Failed to generate updated report:', error);
      this.__updatedReport = `Error generating report: ${error.message}`;
    } finally {
      this.__generatingReport = false;
      this.requestUpdate();
    }
  }

  __getReportOptions() {
    return [
      { value: '', label: 'Select ERA Report' },
      ...this.__eraReports.map(report => ({
        value: report.id,
        label: report.name,
      })),
    ];
  }

  static get styles() {
    return [
      baseStyles,
      css`
        :host {
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
          background-color: ${CSS_COLOR_WHITE};
        }

        .container {
          display: flex;
          flex-direction: column;
          padding: 5px 20px;
        }

        .era-select-container {
          margin-bottom: ${CSS_SPACING};
        }

        .era-select-container label {
          display: block;
          font-weight: bold;
          margin-bottom: 8px;
        }

        .era-select-container select {
          width: 100%;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
        }

        .era-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${CSS_SPACING};
          margin-top: ${CSS_SPACING};
        }

        .era-tree-container {
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: ${CSS_SPACING};
        }

        .era-tree-title {
          font-weight: bold;
          margin-bottom: ${CSS_SPACING};
        }

        .era-tree-view {
          max-height: 400px;
          overflow-y: auto;
        }

        .tree-structure {
          font-family: monospace;
        }

        .tree-node {
          cursor: pointer;
          padding: 4px 8px;
          margin: 2px 0;
          border-radius: 4px;
          transition: background-color 0.2s;
        }

        .tree-node:hover {
          background-color: #f0f0f0;
        }

        .tree-node.selected {
          background-color: #e3f2fd;
        }

        .era-node {
          font-weight: bold;
          color: #1976d2;
        }

        .claim-node {
          margin-left: 20px;
          color: #388e3c;
        }

        .line-item-node {
          margin-left: 40px;
          color: #f57c00;
        }

        .tree-children {
          margin-left: 16px;
        }

        .era-warnings-panel {
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: ${CSS_SPACING};
        }

        .warnings-title {
          font-weight: bold;
          margin-bottom: ${CSS_SPACING};
        }

        .warnings-container {
          max-height: 300px;
          overflow-y: auto;
          margin-bottom: ${CSS_SPACING};
        }

        .warnings-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .selected-node-info {
          background-color: #f5f5f5;
          padding: 8px;
          border-radius: 4px;
          margin-bottom: 12px;
        }

        .warning-item {
          padding: 4px 0;
        }

        .textarea {
          margin-top: ${CSS_SPACING};
          width: 100%;
          height: 200px;
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="container">
        <div class="era-select-container">
          <label for="${ELEMENTS.eraReportSelect.id}">Select ERA Report</label>
          <select
            id="${ELEMENTS.eraReportSelect.id}"
            @change="${this.__handlers.selectEraReport}"
            ?disabled="${this.__loading}"
          >
            ${this.__loading
              ? html`<option>Loading reports...</option>`
              : this.__getReportOptions().map(
                  option => html`
                    <option value="${option.value}">${option.label}</option>
                  `,
                )}
          </select>
        </div>

        ${this.__selectedEraReport
          ? html`
              <div class="era-content">
                <div class="era-tree-container">
                  <div class="era-tree-title">ERA Structure</div>
                  <div id="${ELEMENTS.eraTreeView.id}" class="era-tree-view">
                    ${this.__renderEraTree()}
                  </div>
                </div>

                <div class="era-warnings-panel">
                  <div class="warnings-title">Warnings</div>
                  ${this.__renderSelectedNodeInfo()}
                  <div
                    id="${ELEMENTS.eraWarningsContainer.id}"
                    class="warnings-container"
                  >
                    ${this.__renderWarningsList()}
                  </div>

                  <neb-button
                    id="${ELEMENTS.getUpdatedReportButton.id}"
                    label="${this.__generatingReport
                      ? 'Generating Report...'
                      : 'Get Updated Report'}"
                    .onClick="${this.__handlers.getUpdatedReport}"
                    ?disabled="${this.__generatingReport}"
                  ></neb-button>

                  <neb-button
                    id="${ELEMENTS.processReportButton.id}"
                    label="${this.__processing
                      ? 'Processing...'
                      : 'Process Report'}"
                    .onClick="${this.__handlers.processReport}"
                    ?disabled="${this.__processing || !this.__updatedReport}"
                  ></neb-button>

                  ${this.__updatedReport
                    ? html`
                        <neb-textarea
                          id="${ELEMENTS.updatedReportTextArea.id}"
                          .name="${ELEMENTS.updatedReportTextArea.id}"
                          class="textarea"
                          .value="${this.__updatedReport}"
                          maxLength="500000"
                          showCount
                        ></neb-textarea>
                      `
                    : ''}
                </div>
              </div>
            `
          : ''}
      </div>
    `;
  }
}

customElements.define('neb-era-warnings-playground', NebEraWarningsPlayground);
